<template>
  <component
    :is="targetBlock.viewComponent"
    v-if="displayState === 'ready' && targetBlock"
    :uuid="uuid"
    :block-id="targetBlock.id"
    :block-uuid="targetBlock.uuid"
    :form-data="targetBlock.editorData"
    :block-type="targetBlock.type"
    @loaded="onLoaded"
  />
  <div
    v-else-if="pageEditorStore.isEditMode"
    :id="uuid"
    class="rounded-xl border border-dashed border-red-300 bg-red-50 p-6 text-center text-red-700 md:p-8"
    :style="{ 'scroll-margin-top': pageEditorStore.scrollMarginTop }"
  >
    <div class="font-bold">
      {{ statusMessage }}
    </div>
    <div v-if="targetBlockUuid" class="mt-1 break-all text-sm text-red-600">
      UUID: {{ targetBlockUuid }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { EditorBlock, PageBlock } from '~/models/page-editor'
import { BlockType, usePageEditor } from '~/stores/page-editor'

type PortalState = 'error' | 'found' | 'missing' | 'not-found' | 'portal-target'

interface PortalResult {
  block?: PageBlock
  isEditMode: boolean
  requestedUuid: string
  state: PortalState
}

interface FormDataInterface {
  targetBlockUuid?: string
}

interface PropsInterface {
  blockUuid: string
  formData: FormDataInterface
  uuid: string
}

const props = defineProps<PropsInterface>()
const emit = defineEmits(['loaded'])
const { t } = useI18n()
const pageEditorStore = usePageEditor()

const targetBlockUuid = computed(
  () => props.formData.targetBlockUuid?.trim() || ''
)

const asyncDataKey = `portal:${props.blockUuid}`
const { data: portalResult, status: requestStatus } =
  useAsyncData<PortalResult>(
    asyncDataKey,
    async () => {
      const requestedUuid = targetBlockUuid.value
      const isEditMode = pageEditorStore.isEditMode

      if (!requestedUuid) {
        return { isEditMode, requestedUuid, state: 'missing' }
      }

      try {
        const block = await pageEditorStore.getPageBlock(
          requestedUuid,
          isEditMode
        )

        if (block.type === BlockType.Portal) {
          return { isEditMode, requestedUuid, state: 'portal-target' }
        }

        return { block, isEditMode, requestedUuid, state: 'found' }
      } catch (error: any) {
        return {
          isEditMode,
          requestedUuid,
          state:
            error.status === 404 || error.statusCode === 404
              ? 'not-found'
              : 'error',
        }
      }
    },
    {
      watch: [targetBlockUuid, () => pageEditorStore.isEditMode],
    }
  )

const isCurrentResult = computed(
  () =>
    portalResult.value?.requestedUuid === targetBlockUuid.value &&
    portalResult.value?.isEditMode === pageEditorStore.isEditMode
)

const targetBlock = computed<EditorBlock | null>(() => {
  if (
    !isCurrentResult.value ||
    portalResult.value?.state !== 'found' ||
    !portalResult.value.block
  ) {
    return null
  }

  try {
    const block = pageEditorStore.pageBlockToEditorBlock(
      portalResult.value.block
    )

    return block.viewComponent ? block : null
  } catch {
    return null
  }
})

const displayState = computed(() => {
  if (!isCurrentResult.value || requestStatus.value === 'pending') {
    return 'loading'
  }

  if (portalResult.value?.state !== 'found') {
    return portalResult.value?.state || 'error'
  }

  if (!portalResult.value.block?.is_visible) {
    return 'hidden'
  }

  return targetBlock.value ? 'ready' : 'unsupported'
})

const statusMessage = computed(() => {
  const messageKeyByState: Record<string, string> = {
    error: 'admin.portal_error',
    hidden: 'admin.portal_hidden',
    loading: 'admin.portal_loading',
    missing: 'admin.portal_missing_uuid',
    'not-found': 'admin.portal_not_found',
    'portal-target': 'admin.portal_target_is_portal',
    unsupported: 'admin.portal_unsupported',
  }

  return t(messageKeyByState[displayState.value] || 'admin.portal_error')
})

const onLoaded = (...args: any[]) => {
  emit('loaded', ...args)
}
</script>
